import { getLocalApiOrigin } from '../localApi';
import { isWeb } from '../platform';

const API = () => `${getLocalApiOrigin()}/api/sessions`;

// Web fallback: no disk-backed /api/sessions endpoint exists, so saves
// live in localStorage, one key per session. Full blobs are stored (they
// double as their own list rows, which is all loadSessionByInstance
// needs). Quota overflows surface as the same 'Failed to save session'
// error the desktop path throws.
const WEB_SESSION_PREFIX = 'valkenhall.session.';

function webListSessions() {
  const out = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(WEB_SESSION_PREFIX)) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed && typeof parsed === 'object') out.push(parsed);
    } catch {}
  }
  return out;
}

export async function listGameSessions() {
  if (isWeb()) return webListSessions();
  const res = await fetch(API());
  if (!res.ok) throw new Error('Failed to list sessions');
  return res.json();
}

export async function saveGameSession(session) {
  if (isWeb()) {
    const id = session?.id || (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
    const record = { ...session, id };
    try {
      localStorage.setItem(`${WEB_SESSION_PREFIX}${id}`, JSON.stringify(record));
    } catch {
      throw new Error('Failed to save session');
    }
    return record;
  }
  const res = await fetch(API(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(session),
  });
  if (!res.ok) throw new Error('Failed to save session');
  return res.json();
}

export async function loadGameSession(sessionId) {
  if (isWeb()) {
    const raw = localStorage.getItem(`${WEB_SESSION_PREFIX}${sessionId}`);
    if (!raw) throw new Error('Failed to load session');
    return JSON.parse(raw);
  }
  const res = await fetch(`${API()}/${encodeURIComponent(sessionId)}`);
  if (!res.ok) throw new Error('Failed to load session');
  return res.json();
}

// Quiet variant for probe-then-load patterns where missing-file is a
// normal outcome (e.g. "is there a resume save for this draft round?").
// Calling loadGameSession directly produces a browser-emitted 404 log
// even when the caller .catch()es it, because the fetch went out and
// the response status is 4xx. Checking the list first avoids the
// request when the id isn't present, eliminating the console noise.
// Returns null on any failure (list error, missing id, load error).
export async function loadGameSessionIfExists(sessionId) {
  if (!sessionId) return null;
  try {
    const all = await listGameSessions();
    if (!all.some((s) => s?.id === sessionId)) return null;
    return await loadGameSession(sessionId);
  } catch {
    return null;
  }
}

export async function deleteGameSession(sessionId) {
  if (isWeb()) {
    localStorage.removeItem(`${WEB_SESSION_PREFIX}${sessionId}`);
    return;
  }
  const res = await fetch(`${API()}/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error('Failed to delete session');
}

// Look up a local save blob by the server-side instance id it's
// associated with. Returns null when the user doesn't host this
// instance (guests have no local blob. Only the creator owns the
// save under the host-gated resumption model).
//
// Fast path: the /api/sessions list response includes instanceId
// per row (see electron/server.js). Fallback path (for a stale
// Electron main process that wasn't restarted after the list-
// endpoint change landed, or for saves written before the
// instanceId stamp was added): open each session file individually
// and check its full blob for instanceId. Slower (N round-trips)
// but guarantees the lookup works regardless of list-endpoint
// version.
export async function loadSessionByInstance(instanceId, userId = null) {
  if (!instanceId) return null;
  const all = await listGameSessions();
  // When a userId is supplied, require a match. Legacy blobs
  // without a userId stamp are treated as "unknown author" and
  // skipped. Accepting them risks returning a teammate's blob on
  // a shared-SESSIONS_DIR dev machine, which is the exact
  // collision this userId-scoping was added to fix.
  const matches = (s) => {
    if (s?.instanceId !== instanceId) return false;
    if (!userId) return true;
    return s?.userId === userId;
  };
  const fast = all.find(matches);
  if (fast) return fast;
  for (const summary of all) {
    if (!summary?.id) continue;
    try {
      const full = await loadGameSession(summary.id);
      if (full?.instanceId !== instanceId) continue;
      if (userId && full?.userId !== userId) continue;
      return { ...summary, instanceId, userId: full.userId || null };
    } catch {}
  }
  return null;
}
